// Centralized "what goes into the bootstrap pipeline" list. Adding a new
// version-tracked (Layer A) store = wire its register call here. Adding a
// new runtime (Layer B) store = push it into `getLayerBStores()`.
//
// Strategies use these helpers so online/offline paths don't drift.

import type { BootContext, LayerBStore } from "./types";
import { useSettingsStore } from "~/stores/settings";
import { usePosEmployeesStore } from "~/stores/pos.employees";
import { usePosProductsStore } from "~/stores/pos.products";
import { usePosShopStore } from "~/stores/pos.shop";
import { usePosPaymentStore } from "~/stores/pos.payment";
import { usePosJobsStore } from "~/stores/pos.jobs";
import { usePosReceiptsStore } from "~/stores/pos.receipts";
import { usePosTableStatesStore } from "~/stores/pos.tableStates";
import { usePosBillsNumberStore } from "~/stores/pos.billsNumber";
import { usePosSoldoutStore } from "~/stores/pos.soldout";
import { usePosProductComboSetStore } from "~/stores/pos.product-comboset";
import { useStockDataCacheStore } from "~/stores/stockDataCache";

/**
 * Phase A — every Layer A store registers its version-tracked resources
 * with versionSync. No network yet. Pure registration; the reconcile pass
 * (strategy-specific) handles fetch/cache.
 */
export function registerAllLayerAResources(): void {
  useSettingsStore().init();
  usePosEmployeesStore().init();
  usePosProductsStore().init();
  usePosShopStore().init();
  usePosPaymentStore().init();
  // Stock user PINs (resource key `stockUserPin`) — version-tracked so a PIN
  // edit on one terminal propagates to the others on their next boot (see
  // Tab4StockPin write path).
  useStockDataCacheStore().init();
}

/**
 * Wire the shop store's `storeId` from the boot context. Bootstrap calls this
 * once it has resolved the authenticated storeId, so all shop-store boot
 * wiring lives here alongside its resource registration above.
 */
export function wireShopStore(ctx: BootContext): void {
  usePosShopStore().storeId = ctx.storeId;
}

/**
 * Phase C — last-mile work after resources are reconciled. Each store may
 * compute derived state or load non-version-tracked auxiliaries (e.g.
 * settings.finishLoad loads dynamoSettings).
 */
export async function finalizeAllLayerAResources(): Promise<void> {
  usePosProductsStore().finishLoad();
  usePosEmployeesStore().finishLoad();
  await useSettingsStore().finishLoad();
  // Combo recipes (DynamoDB, cache-first). Loaded here in Phase C so comboHolder
  // is populated before Layer B (jobs / mobile / terminal listeners) can process
  // any incoming order that needs expansion.
  await usePosProductComboSetStore().finishLoad();
}

/**
 * Layer B runtime stores in boot order. Listed explicitly so adding a new
 * one is a deliberate edit (vs. auto-discovery which hides ordering bugs).
 *
 * Order within the array does NOT imply sequential boot — strategies call
 * `bootOnline`/`bootOffline` in parallel via `Promise.all`. List order only
 * matters for logging.
 */
export function getLayerBStores(): LayerBStore[] {
  return [
    usePosJobsStore() as unknown as LayerBStore,
    usePosReceiptsStore() as unknown as LayerBStore,
    usePosTableStatesStore() as unknown as LayerBStore,
    usePosBillsNumberStore() as unknown as LayerBStore,
    usePosSoldoutStore() as unknown as LayerBStore,
  ];
}

/**
 * The POS used to mirror every sale into the legacy MSSQL `Orders` table via
 * `POST /orders`, alongside the DynamoDB receipt write. DynamoDB is the source
 * of truth and that mirror write is gone, so terminals no longer produce
 * `receipt-order:*` entries -- but devices that ran the old build still carry
 * whatever never synced. Nothing reads those keys any more, so sweep them once
 * on boot instead of leaving dead payloads on the device forever.
 */
export function purgeLegacyReceiptOrderCache(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("receipt-order")) stale.push(key);
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("Failed to purge legacy receipt-order cache:", error);
  }
}
